/**
 * ================================================================
 * FirstCut - AI Status Indicator Component
 * ================================================================
 * Shows users the current AI system status in the sidebar.
 * Polls the health endpoint periodically and displays:
 * - 🟢 Operational - All systems working
 * - 🟡 Degraded - Some delays expected
 * - 🔴 Down - Temporary outage
 * ================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiWifi, FiWifiOff, FiAlertTriangle } from 'react-icons/fi';
import { api } from '../services/api';

const STATUS_CONFIG = {
  operational: {
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    icon: FiWifi,
    label: 'AI Online',
    shortLabel: '●'
  },
  degraded: {
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    icon: FiAlertTriangle,
    label: 'AI Busy',
    shortLabel: '●'
  },
  down: {
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    icon: FiWifiOff,
    label: 'AI Offline',
    shortLabel: '●'
  }
};

function AIStatusIndicator({ collapsed = false }) {
  const [status, setStatus] = useState('operational');
  const [message, setMessage] = useState('All systems operational');
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const checkHealth = useCallback(async () => {
    try {
      const response = await api.checkAIHealth();
      setStatus(response.status || 'operational');
      setMessage(response.message || 'All systems operational');
      setLastChecked(new Date());
    } catch (err) {
      // If health check fails, assume degraded
      console.warn('AI health check failed:', err);
      setStatus('degraded');
      setMessage('Unable to verify AI status');
      setLastChecked(new Date());
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkHealth();

    // Poll every 30 seconds
    const interval = setInterval(checkHealth, 30000);

    return () => clearInterval(interval);
  }, [checkHealth]);

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.operational;
  const Icon = config.icon;

  // Only show when not operational (to avoid visual noise)
  if (status === 'operational') {
    return null;
  }

  return (
    <div 
      className={`relative ${collapsed ? 'flex justify-center' : ''}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg
          ${config.bgColor} ${config.borderColor} border
          transition-all cursor-default
          ${collapsed ? 'w-10 h-10 justify-center' : 'w-full'}
        `}
        data-testid="ai-status-indicator"
      >
        <Icon className={`${config.color} flex-shrink-0`} size={collapsed ? 18 : 16} />
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-medium ${config.color}`}>{config.label}</p>
            <p className="text-xs text-muted-foreground truncate">{message}</p>
          </div>
        )}
      </div>

      {/* Tooltip for collapsed state */}
      {collapsed && showTooltip && (
        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50">
          <div className={`
            px-3 py-2 rounded-lg shadow-lg border
            ${config.bgColor} ${config.borderColor}
            whitespace-nowrap
          `}>
            <p className={`text-sm font-medium ${config.color}`}>{config.label}</p>
            <p className="text-xs text-muted-foreground">{message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIStatusIndicator;
